import { Asset } from '@/@shared/components/primitives/Asset';
import styles from '@/@shared/components/forms/Checkbox/Checkbox.module.scss';
import { useTransAuth } from '@/@shared/hooks/useTranslations';
import { clc } from '@/@shared/utils/classComposer';
import { smoothScrollToCenter } from '@/@shared/utils/scroll';
import Link from 'next/link';
import type React from 'react';
import { useFormContext } from 'react-hook-form';

interface AuthCheckboxProps {
	keyProp: string;
	label: string;

	register: {
		ref: (instance: HTMLInputElement | null) => void;
	};

	className?: string;
	frameClass?: string;
	labelClass?: string;
	children?: React.ReactNode;
	viewLink?: string;
	required?: boolean;
	optional?: boolean;

	// Roving Tabindex 관련 (그룹 모드)
	groupMode?: boolean;
	tabIndex?: number;
	onKeyDown?: (e: React.KeyboardEvent<HTMLLabelElement>) => void;
	onFocus?: () => void;
}

interface AllAuthCheckboxProps {
	keyProp: string;
	label: string;
	checked: boolean;

	handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	className?: string;
	frameClass?: string;
	labelClass?: string;
	children?: React.ReactNode;
	viewLink?: string;
	required?: boolean;
	optional?: boolean;

	// Roving Tabindex 관련 (그룹 모드)
	groupMode?: boolean;
	tabIndex?: number;
	onKeyDown?: (e: React.KeyboardEvent<HTMLLabelElement>) => void;
	onFocus?: () => void;
}

export const AllAuthCheckbox = ({
	keyProp,
	label,
	checked,
	className,
	frameClass,
	labelClass,
	viewLink,
	required,
	optional,
	handleChange,
	// Roving Tabindex props
	groupMode = false,
	tabIndex: externalTabIndex,
	onKeyDown: externalOnKeyDown,
	onFocus: externalOnFocus,
}: AllAuthCheckboxProps) => {
	const a = useTransAuth('signup.agreements');

	const handleKeyDownStandalone = (e: React.KeyboardEvent<HTMLLabelElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			const syntheticEvent = {
				target: { checked: !checked },
			} as React.ChangeEvent<HTMLInputElement>;
			handleChange(syntheticEvent);
		}
	};

	// 실제 사용할 핸들러 결정 (SOLID: Open/Closed Principle)
	const handleKeyDown = groupMode ? externalOnKeyDown : handleKeyDownStandalone;
	const handleFocus = groupMode ? externalOnFocus : undefined;
	const computedTabIndex = groupMode ? (externalTabIndex ?? -1) : 0;

	return (
		<div className={clc(frameClass)}>
			<label
				className={clc(styles.checkboxLabel)}
				tabIndex={computedTabIndex}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				role="checkbox"
				aria-checked={checked}
			>
				<Asset
					type="icon"
					name={checked ? 'auth-action-checkbox-active-svg' : 'auth-action-checkbox-default-white-svg'}
					size="md"
					className={clc(styles.checkbox, className)}
					alt=""
				/>
				<input
					checked={checked}
					className={styles.checkbox}
					key={keyProp}
					onChange={(event) => handleChange(event)}
					onFocus={(e) => {
						smoothScrollToCenter(e.currentTarget);
					}}
					style={{ display: 'none' }}
					type="checkbox"
					aria-hidden="true"
				/>
				<div className={clc(styles.text, labelClass)}>
					{required && <span className={styles.required}>{a('required')}</span>}
					{optional && <span className={styles.optional}>{a('optional')}</span>}
					{label}
				</div>
			</label>
			{viewLink && (
				<Link
					className={styles.view}
					href={viewLink}
					target="_blank"
					rel="noopener noreferrer"
					aria-label={`View ${label} details (opens in new window)`}
				>
					<Asset type="icon" name="auth-chevron-right-svg" size="md" className={styles.icon} alt="" />
				</Link>
			)}
		</div>
	);
};

const AuthCheckbox = ({
	keyProp,
	label,

	register,
	className,
	frameClass,
	labelClass,
	children,
	viewLink,
	required,
	optional,
	// Roving Tabindex props
	groupMode = false,
	tabIndex: externalTabIndex,
	onKeyDown: externalOnKeyDown,
	onFocus: externalOnFocus,
}: AuthCheckboxProps) => {
	const a = useTransAuth('signup.agreements');
	const { watch, setValue } = useFormContext();

	const checked = watch(keyProp);

	const handleKeyDownStandalone = (e: React.KeyboardEvent<HTMLLabelElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			setValue(keyProp, !checked);
		}
	};

	// 실제 사용할 핸들러 결정 (SOLID: Open/Closed Principle)
	const handleKeyDown = groupMode ? externalOnKeyDown : handleKeyDownStandalone;
	const handleFocus = groupMode ? externalOnFocus : undefined;
	const computedTabIndex = groupMode ? (externalTabIndex ?? -1) : 0;

	return (
		<div className={clc(frameClass)}>
			<label
				className={clc(styles.checkboxLabel)}
				tabIndex={computedTabIndex}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				role="checkbox"
				aria-checked={checked}
			>
				<Asset
					type="icon"
					name={checked ? 'auth-action-checkbox-active-svg' : 'auth-action-checkbox-default-white-svg'}
					size="md"
					className={clc(styles.checkbox, className)}
					alt=""
				/>
				<input
					className={styles.checkbox}
					defaultChecked={checked}
					key={keyProp}
					type="checkbox"
					{...register}
					style={{ display: 'none' }}
					aria-hidden="true"
				/>
				<div className={clc(styles.text, labelClass)}>
					{required && <span className={styles.required}>{a('required')}</span>}
					{optional && <span className={styles.optional}>{a('optional')}</span>}
					{label}
				</div>
			</label>
			{viewLink && (
				<Link
					className={styles.view}
					href={viewLink}
					target="_blank"
					rel="noopener noreferrer"
					aria-label={`View ${label} details (opens in new window)`}
				>
					<Asset type="icon" name="auth-chevron-right-svg" size="md" className={styles.icon} alt="" />
				</Link>
			)}
		</div>
	);
};

export default AuthCheckbox;
